import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const LABEL = { reservation_request: "Booking request", inquiry: "Enquiry", alteration_request: "Change request" };
const DONE = {
  accepted: "Accepted", declined: "Declined", accept: "Accepted", decline: "Declined",
  preapproval: "Pre-approved", special_offer: "Special offer sent", canceled: "Withdrawn by guest",
  cancelled: "Withdrawn by guest", expired: "Expired",
};

// Airbnb request waiting on the host (request to book, enquiry, change request), answered here.
// Every answer is final on Airbnb, so each one asks for confirmation first.
export default function OtaRequestCard({ thread, onUpdated }) {
  const req = thread?.ota_request;
  const [mode, setMode] = useState(null);          // null | "decline" | "offer"
  const [reason, setReason] = useState("not_a_good_fit");
  const [note, setNote] = useState("");
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);
  if (!req) return null;

  const label = LABEL[req.kind] || "Request";
  const pending = req.status === "pending";
  const deadline = req.received_at ? new Date(new Date(req.received_at + (req.received_at.endsWith("Z") ? "" : "Z")).getTime() + 24 * 3600 * 1000) : null;
  const hoursLeft = deadline ? Math.max(0, Math.round((deadline - Date.now()) / 3600000)) : null;

  const answer = async (action, extra = {}, confirmText) => {
    if (!window.confirm(`${confirmText}\n\nThis is final on Airbnb and cannot be changed afterwards.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/messages/${thread.id}/ota-request`, { action, ...extra });
      onUpdated?.(data);
      setMode(null);
      toast.success("Sent to Airbnb");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send your answer, please retry");
    } finally { setBusy(false); }
  };

  const btn = "px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50";
  return (
    <div className="mx-4 mt-3 rounded-xl border border-rose-200 dark:border-rose-900/50 bg-rose-50/60 dark:bg-rose-950/20 p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-xs font-bold text-rose-700 dark:text-rose-300">{label} on Airbnb</span>
        {pending ? (
          hoursLeft !== null && <span className="text-[10px] font-semibold text-amber-700 dark:text-amber-400">{hoursLeft}h left to answer</span>
        ) : (
          <span className="text-[10px] font-semibold text-slate-500">{DONE[req.status] || req.status}</span>
        )}
      </div>
      <p className="text-xs text-slate-600 dark:text-slate-300">
        {req.check_in || "?"} → {req.check_out || "?"}
        {req.guests ? ` · ${req.guests} guests` : ""}
        {req.amount ? ` · ${req.currency === "INR" ? "₹" : `${req.currency} `}${Number(req.amount).toLocaleString("en-IN")}` : ""}
      </p>

      {pending && mode === null && (
        <div className="flex flex-wrap gap-2">
          {req.kind === "inquiry" ? (<>
            <button disabled={busy} className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700`}
              onClick={() => answer("preapprove", {}, "Pre-approve this guest at your current price?")}>Pre-approve</button>
            <button disabled={busy} className={`${btn} bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200`}
              onClick={() => setMode("offer")}>Send special offer</button>
          </>) : (<>
            <button disabled={busy} className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700`}
              onClick={() => answer("accept", {}, `Accept this ${label.toLowerCase()}?`)}>Accept</button>
            <button disabled={busy} className={`${btn} bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200`}
              onClick={() => req.kind === "reservation_request" ? setMode("decline") : answer("decline", {}, "Decline this change request?")}>Decline</button>
          </>)}
        </div>
      )}

      {pending && mode === "decline" && (
        <div className="space-y-2">
          <select value={reason} onChange={e => setReason(e.target.value)}
            className="w-full text-xs px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200">
            <option value="not_a_good_fit">Not a good fit for this stay</option>
            <option value="dates_not_available">Dates not available (Airbnb keeps them blocked)</option>
            <option value="waiting_for_better_reservation">Waiting for a better reservation</option>
            <option value="not_comfortable">Not comfortable with this request</option>
          </select>
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} maxLength={1000}
            placeholder="Message to the guest (optional)"
            className="w-full text-xs px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200" />
          <div className="flex gap-2">
            <button disabled={busy} className={`${btn} bg-rose-600 text-white hover:bg-rose-700`}
              onClick={() => answer("decline", { reason, message_to_guest: note }, "Decline this booking request?")}>Decline request</button>
            <button disabled={busy} className={`${btn} text-slate-500`} onClick={() => setMode(null)}>Back</button>
          </div>
        </div>
      )}

      {pending && mode === "offer" && (
        <div className="space-y-2">
          <p className="text-[11px] text-slate-500">Total for the whole stay in rupees, cleaning fee included, no taxes. Airbnb deducts its service fee from it.</p>
          <div className="flex gap-2">
            <input type="number" inputMode="numeric" min="1" value={price} onChange={e => setPrice(e.target.value)} placeholder="e.g. 9000"
              className="w-32 text-xs px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200" />
            <button disabled={busy || !(Number(price) > 0)} className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700`}
              onClick={() => answer("special_offer", { total_price: Math.round(Number(price)) }, `Send a special offer of ₹${Number(price).toLocaleString("en-IN")} for the whole stay?`)}>Send offer</button>
            <button disabled={busy} className={`${btn} text-slate-500`} onClick={() => setMode(null)}>Back</button>
          </div>
        </div>
      )}
    </div>
  );
}
